import { getData, putData } from '~/utils/service';
import type { Scope } from './types';
import { CoverageSubmitResult, OnSubmitEvent } from './types';

const STATES_ROUTE_NAME = 'msaasListingStates';
const GEOGRAPHIES_ROUTE_NAME = 'msaasListingGeographies';
const PROVIDERS_ROUTE_NAME = 'msaasListingProviders';

type RosterResponse = { providers: Array<{ providerId: number }> };

/** The roster's provider ids (the owner sees names too, but the form only needs the ids). */
export const loadRosterProviderIds = async (listingId: number): Promise<number[]> => {
  const response = await getData<RosterResponse>(PROVIDERS_ROUTE_NAME, {
    pathParams: { listingId: listingId.toString() },
  });
  return (response?.providers ?? []).map((provider) => provider.providerId);
};

// One save writes what applies to the work location, in order, and stops at the first failure. The
// actor's signal is aborted when the wizard leaves the step; that cancels the write in flight and
// stops the rest, so an old save cannot overwrite what was edited after the listing was reopened.
export const submitCoverage = async ({
  input,
  signal,
}: {
  input: Scope;
  signal?: AbortSignal;
}): Promise<CoverageSubmitResult> => {
  const { payload } = input.event as OnSubmitEvent;
  const pathParams = { listingId: input.context.listingId.toString() };
  const result: CoverageSubmitResult = {
    stateCodes: payload.stateCodes,
    geographies: payload.geographies,
    providerIds: payload.providerIds ?? undefined,
  };
  // What the writes so far landed, so a failure part-way can tell the listing what is now saved.
  const saved: Partial<CoverageSubmitResult> = {};
  try {
    await writeCoverage(payload, pathParams, result, saved, signal);
  } catch (error) {
    if (error !== null && typeof error === 'object')
      (error as { savedCoverage?: Partial<CoverageSubmitResult> }).savedCoverage = saved;
    throw error;
  }
  return result;
};

const writeCoverage = async (
  payload: OnSubmitEvent['payload'],
  pathParams: { listingId: string },
  result: CoverageSubmitResult,
  saved: Partial<CoverageSubmitResult>,
  signal: AbortSignal | undefined
) => {
  if (payload.persistStates) {
    signal?.throwIfAborted();
    await putData(STATES_ROUTE_NAME, {
      data: { stateCodes: payload.stateCodes },
      pathParams,
      signal,
    });
    saved.stateCodes = payload.stateCodes;
  }
  if (payload.persistGeographies) {
    signal?.throwIfAborted();
    const reply = await putData<{ geographies?: CoverageSubmitResult['geographies'] } | unknown[]>(
      GEOGRAPHIES_ROUTE_NAME,
      { data: { geographies: payload.geographies }, pathParams, signal }
    );
    // The reply carries the coordinates the server resolved; keep the request's list if it is bare.
    const list = Array.isArray(reply) ? reply : reply?.geographies;
    if (Array.isArray(list)) result.geographies = list as CoverageSubmitResult['geographies'];
    saved.geographies = result.geographies;
  }
  if (payload.providerIds && payload.persistProviders !== false) {
    signal?.throwIfAborted();
    await putData(PROVIDERS_ROUTE_NAME, {
      data: { providerIds: payload.providerIds },
      pathParams,
      signal,
    });
    saved.providerIds = payload.providerIds;
  }
};
